/**
 * `/contact`: a message to the person running the site.
 *
 * Only the message is required. The address is optional and asked for only so
 * the reader can be answered. The form posts to POST /api/contact, which
 * checks Turnstile and sends the message by mail.
 *
 * A message that did not go is never shown as sent: the reader is told, and
 * what they wrote stays in the box.
 */

import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router'
import { isMessageKey, type MessageKey } from '../../shared/message-keys.ts'
import { safeReturnPath } from '../../shared/return-path.ts'
import { Alert } from '../components/Alert.tsx'
import { usePageTitle } from '../components/Catalogue.tsx'
import { Field } from '../components/Field.tsx'
import { Turnstile } from '../components/Turnstile.tsx'
import { useI18n } from '../i18n/index.tsx'
import { fill } from '../lib/catalogue.ts'
import { useClientConfig } from '../lib/use-client-config.ts'

export function Contact() {
  const { t, locale } = useI18n()
  const config = useClientConfig()
  usePageTitle(t('contact.title'))

  // Arriving from „Съобщете за грешка“: the page it was about, as a path on
  // this site or nothing (shared/return-path.ts), named at the top of the message.
  const [params] = useSearchParams()
  const about = safeReturnPath(params.get('about'))
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState(() => (about === null ? '' : `${fill(t('report.about'), { page: `${window.location.origin}${about}` })}\n\n`))
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileResets, setTurnstileResets] = useState(0)
  const [error, setError] = useState<MessageKey | null>(null)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (message.trim() === '') {
      setError('error.message_required')
      return
    }
    if (turnstileToken === '') {
      setError('error.turnstile_missing')
      return
    }

    setBusy(true)
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name, email, message, turnstileToken }),
      })
      const body = (await response.json().catch(() => null)) as { ok?: boolean; error?: { key?: string } } | null
      if (response.ok && body?.ok === true) {
        setSent(true)
        return
      }
      const key = body?.error?.key
      setError(key !== undefined && isMessageKey(key) ? key : 'error.contact_failed')
    } catch {
      setError('error.contact_failed')
    } finally {
      setBusy(false)
      // A Turnstile token works once, so every attempt needs a fresh one.
      setTurnstileToken('')
      setTurnstileResets((n) => n + 1)
    }
  }

  if (sent) {
    return (
      <section className="panel">
        <h1>{t('contact.sent.title')}</h1>
        <p>{t(email.trim() === '' ? 'contact.sent.body_anonymous' : 'contact.sent.body')}</p>
      </section>
    )
  }

  return (
    <section className="panel">
      <h1>{t('contact.title')}</h1>
      <p>{t('contact.lead')}</p>

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field
          label={t('contact.name.label')}
          type="text"
          name="name"
          autoComplete="name"
          maxLength={100}
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
        />
        <Field
          label={t('contact.email.label')}
          hint={t('contact.email.hint')}
          type="email"
          name="email"
          autoComplete="email"
          maxLength={254}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
          }}
        />
        <div className="field">
          <label className="field__label" htmlFor="contact-message">
            {t('contact.message.label')}
          </label>
          <textarea
            id="contact-message"
            className="field__input field__input--area"
            name="message"
            rows={8}
            maxLength={5000}
            required
            value={message}
            onChange={(event) => {
              setMessage(event.target.value)
            }}
          />
        </div>

        {config ? (
          <Turnstile siteKey={config.turnstileSiteKey} locale={locale} onToken={setTurnstileToken} resetSignal={turnstileResets} />
        ) : null}

        {error ? <Alert tone="error">{t(error)}</Alert> : null}

        <button type="submit" className="button" disabled={busy}>
          {t(busy ? 'contact.sending' : 'contact.submit')}
        </button>
      </form>
    </section>
  )
}
